import React, { useState, useCallback, useEffect } from 'react';
import { WalletMultiButton, WalletDisconnectButton } from '@solana/wallet-adapter-react-ui';
import { useWallet } from '@solana/wallet-adapter-react';
import { BackendAuthService } from '../../services/backendAuth';
import { useAuth } from '../../contexts/AuthContext';
import { SignatureDiagnosticPanel } from '../UI/SignatureDiagnosticPanel';

export const WalletConnect: React.FC = () => {
  const { connected, publicKey, signMessage } = useWallet();
  const { user, setUser } = useAuth();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backendStatus, setBackendStatus] = useState<'checking' | 'connected' | 'disconnected'>('checking');
  const [showDiagnostic, setShowDiagnostic] = useState(false);

  // Debug: afficher l'état du wallet
  console.log('🔍 État wallet:', {
    connected,
    publicKey: publicKey?.toString(),
    hasSignMessage: !!signMessage,
    user: user?.username
  });

  // Vérifier la connexion backend au chargement
  useEffect(() => {
    const checkBackend = async () => {
      try {
        const isConnected = await BackendAuthService.testBackendConnection();
        setBackendStatus(isConnected ? 'connected' : 'disconnected');
      } catch (error) {
        console.error('❌ Erreur lors de la vérification backend:', error);
        setBackendStatus('disconnected');
      }
    };

    checkBackend();
  }, []);

  const handleAuthentication = useCallback(async () => {
    if (!connected || !publicKey || !signMessage) {
      setError('Wallet non connecté ou signature non supportée');
      return;
    }

    setIsAuthenticating(true);
    setError(null);

    try {
      const walletAddress = publicKey.toString();
      const authResponse = await BackendAuthService.signInWithBackend(
        walletAddress,
        signMessage
      );

      setUser(authResponse.user);
      console.log('✅ Authentification réussie:', authResponse.user);
    } catch (error) {
      console.error('❌ Erreur authentification:', error);
      setError(error instanceof Error ? error.message : 'Erreur d\'authentification');
    } finally {
      setIsAuthenticating(false);
    }
  }, [connected, publicKey, signMessage, setUser]);

  // Affichage de l'état du backend
  const BackendStatusIndicator = () => (
    <div className="flex items-center space-x-2 text-xs">
      <div className={`w-2 h-2 rounded-full ${
        backendStatus === 'connected' ? 'bg-emerald-500' : 
        backendStatus === 'disconnected' ? 'bg-red-500' : 'bg-yellow-500 animate-pulse'
      }`}></div>
      <span className="text-emerald-200/60">
        Backend: {
          backendStatus === 'connected' ? 'Connecté' : 
          backendStatus === 'disconnected' ? 'Déconnecté' : 'Vérification...'
        }
      </span>
    </div>
  );

  if (!connected) {
    return (
      <div className="flex flex-col items-center space-y-4">
        <div className="flex flex-col items-center space-y-4">
          <h3 className="text-lg font-semibold text-emerald-100">Connecter votre Wallet</h3>
          <p className="text-sm text-emerald-200/80 text-center max-w-sm">
            Connectez votre wallet Solana pour rejoindre la communauté Jupipotes 🚀
          </p>
          <div className="wallet-adapter-button-trigger">
            <WalletMultiButton className="!bg-gradient-to-r !from-emerald-600 !to-cyan-600 hover:!from-emerald-700 hover:!to-cyan-700 !border-emerald-500 !text-white !font-medium !rounded-xl !px-6 !py-3 !transition-all !duration-200 !shadow-lg hover:!shadow-emerald-500/25" />
          </div>
          <BackendStatusIndicator />
        </div>
      </div>
    );
  }

  if (!user) {
    const isButtonDisabled = isAuthenticating || backendStatus !== 'connected' || !signMessage;
    
    // Debug: afficher l'état du bouton
    console.log('🔍 État bouton authentification:', {
      isAuthenticating,
      backendStatus,
      hasSignMessage: !!signMessage,
      isButtonDisabled
    });

    return (
      <div className="flex flex-col items-center space-y-4">
        <div className="flex flex-col items-center space-y-4">
          <p className="text-sm text-emerald-200/80 text-center">
            Wallet connecté: {publicKey?.toString().slice(0, 8)}...{publicKey?.toString().slice(-8)}
          </p>
          
          <button
            onClick={handleAuthentication}
            disabled={isButtonDisabled}
            className={`
              px-6 py-3 rounded-xl font-medium text-sm transition-all duration-200 shadow-lg
              ${isButtonDisabled
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-emerald-600 to-cyan-600 text-white hover:from-emerald-700 hover:to-cyan-700 cursor-pointer hover:shadow-emerald-500/25'
              }
            `}
          >
            {isAuthenticating ? (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>Authentification...</span>
              </div>
            ) : (
              'S\'authentifier avec Jupipotes 🚀'
            )}
          </button>

          {!signMessage && connected && (
            <div className="bg-orange-900/50 border border-orange-500/50 text-orange-200 px-3 py-2 rounded-lg text-xs text-center max-w-sm">
              ⚠️ Votre wallet ne supporte pas la signature de messages. Essayez Phantom ou Solflare.
            </div>
          )}

          {backendStatus !== 'connected' && (
            <p className="text-xs text-red-400 text-center max-w-sm">
              Backend non disponible. Vérifiez votre connexion.
            </p>
          )}
          
          {error && (
            <div className="space-y-2">
              <p className="text-sm text-red-400 text-center max-w-sm bg-red-900/20 border border-red-500/30 rounded-lg p-2">
                {error}
              </p>
              {error.includes('signature') && (
                <button
                  onClick={() => setShowDiagnostic(true)}
                  className="text-xs text-cyan-400 hover:text-cyan-300 underline"
                >
                  🔍 Diagnostiquer le problème
                </button>
              )}
            </div>
          )}
          
          <WalletDisconnectButton className="!bg-red-600/80 hover:!bg-red-700 !text-sm !rounded-lg !px-4 !py-2" />
          <BackendStatusIndicator />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center space-y-4">
      <div className="flex flex-col items-center space-y-3">
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
          <p className="text-sm font-medium text-emerald-200">Authentifié avec succès ! 🎉</p>
        </div>
        <div className="text-center">
          <p className="text-sm text-emerald-100 font-medium">
            @{user.username}
          </p>
          <p className="text-xs text-emerald-200/70">
            {publicKey?.toString().slice(0, 8)}...{publicKey?.toString().slice(-8)}
          </p>
        </div>
        <div className="flex items-center space-x-3 text-xs text-emerald-200/80 bg-emerald-900/30 px-3 py-1 rounded-full">
          <span>Niveau {user.level}</span>
          <span>•</span>
          <span>{user.xp} XP</span>
        </div>
        <WalletDisconnectButton className="!bg-red-600/80 hover:!bg-red-700 !text-sm !rounded-lg !px-4 !py-2" />
        <BackendStatusIndicator />
      </div>

      {/* Panel de diagnostic */}
      <SignatureDiagnosticPanel 
        isOpen={showDiagnostic}
        onClose={() => setShowDiagnostic(false)}
      />
    </div>
  );
};